// VisualizerUtils.js
export default class VisualizerUtils {
  constructor(canvas, colorTheme, themes) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');
    this.width = canvas.width;
    this.height = canvas.height;
    this.colorTheme = colorTheme;
    this.themes = themes;
    this.animationFrame = null;
    this.lastFrameTime = 0;
    this.targetFPS = 60;
    this.frameInterval = 1000 / this.targetFPS;
    this.barWidth = 4;
    this.barGap = 3;
    this.barCount = Math.floor(this.width / (this.barWidth + this.barGap));
    this.barPositions = new Array(this.barCount).fill(0).map((_, i) => ({
      x: i * (this.barWidth + this.barGap),
      position: i / this.barCount
    }));
    this.gradientCache = {};

    // Audio source: AnalyserNode provides real frequency data when set.
    // Without one, renderBars falls back to its procedural animation.
    this.analyser = null;
    this.freqData = null;
  }

  setAnalyser(analyser) {
    this.analyser = analyser;
    if (analyser) {
      this.freqData = new Uint8Array(analyser.frequencyBinCount);
    } else {
      this.freqData = null;
    }
  }

  getThemeColors() {
    // Returns theme colors along with a generic channelColors array.
    if (this.colorTheme === 'black') {
      return {
        gradientColors: ["#0a0a0a", "#1a1a1a", "#0a0a0a"],
        barColorDefault: "#ffffff",
        channelColors: ["rgba(255, 100, 100, {opacity})", "rgba(100, 255, 100, {opacity})", "rgba(100, 100, 255, {opacity})"],
        systemColor: "rgba(255, 255, 100, {opacity})"
      };
    } else if (this.colorTheme === 'blue') {
      return {
        gradientColors: ["#0c1829", "#1e3a5f", "#0c1829"],
        barColorDefault: "rgba(56, 189, 248, {opacity})",
        channelColors: ["rgba(255, 120, 120, {opacity})", "rgba(120, 255, 120, {opacity})", "rgba(120, 120, 255, {opacity})"],
        systemColor: "rgba(255, 255, 100, {opacity})"
      };
    } else if (this.colorTheme === 'purple') {
      return {
        gradientColors: ["#2d1b69", "#4c1d95", "#2d1b69"],
        barColorDefault: "rgba(216, 180, 254, {opacity})",
        channelColors: ["rgba(255, 130, 130, {opacity})", "rgba(130, 255, 130, {opacity})", "rgba(130, 130, 255, {opacity})"],
        systemColor: "rgba(255, 255, 100, {opacity})"
      };
    } else if (this.colorTheme === 'green') {
      return {
        gradientColors: ["#022c22", "#064e3b", "#022c22"],
        barColorDefault: "rgba(52, 211, 153, {opacity})",
        channelColors: ["rgba(255, 140, 140, {opacity})", "rgba(140, 255, 140, {opacity})", "rgba(140, 140, 255, {opacity})"],
        systemColor: "rgba(255, 255, 100, {opacity})"
      };
    } else if (this.colorTheme === 'cyber') {
      return {
        gradientColors: ["#09090b", "#18181b", "#09090b"],
        barColorDefault: ["rgba(244, 114, 182, {opacity})", "rgba(34, 211, 238, {opacity})"],
        channelColors: ["rgba(255, 150, 150, {opacity})", "rgba(150, 255, 150, {opacity})", "rgba(150, 150, 255, {opacity})"],
        systemColor: "rgba(255, 255, 100, {opacity})"
      };
    } else if (this.colorTheme === 'light') {
      return {
        gradientColors: ["#f8fafc", "#e2e8f0", "#f8fafc"],
        barColorDefault: "rgba(71, 85, 105, {opacity})",
        channelColors: ["rgba(239, 68, 68, {opacity})", "rgba(34, 197, 94, {opacity})", "rgba(59, 130, 246, {opacity})"],
        systemColor: "rgba(245, 158, 11, {opacity})"
      };
    } else if (this.colorTheme === 'sunset') {
      return {
        gradientColors: ["#431407", "#7c2d12", "#431407"],
        barColorDefault: "rgba(251, 146, 60, {opacity})",
        channelColors: ["rgba(255, 130, 130, {opacity})", "rgba(255, 200, 130, {opacity})", "rgba(255, 170, 130, {opacity})"],
        systemColor: "rgba(255, 230, 100, {opacity})"
      };
    } else if (this.colorTheme === 'rose') {
      return {
        gradientColors: ["#4c0519", "#881337", "#4c0519"],
        barColorDefault: "rgba(251, 113, 133, {opacity})",
        channelColors: ["rgba(255, 130, 170, {opacity})", "rgba(230, 130, 255, {opacity})", "rgba(255, 150, 200, {opacity})"],
        systemColor: "rgba(255, 200, 130, {opacity})"
      };
    } else if (this.colorTheme === 'sepia') {
      return {
        gradientColors: ["#fef7ed", "#fde6bd", "#fef7ed"],
        barColorDefault: "rgba(161, 98, 7, {opacity})",
        channelColors: ["rgba(180, 60, 40, {opacity})", "rgba(60, 130, 90, {opacity})", "rgba(180, 120, 40, {opacity})"],
        systemColor: "rgba(217, 119, 6, {opacity})"
      };
    }
  }

  // Helper to select a color based on speaker name.
  getColorForSpeaker(speaker, channelColors) {
    let hash = 0;
    for (let i = 0; i < speaker.length; i++) {
      hash += speaker.charCodeAt(i);
    }
    const index = hash % channelColors.length;
    return channelColors[index];
  }

  getOrCreateGradient() {
    const cacheKey = this.colorTheme;
    if (this.gradientCache[cacheKey]) return this.gradientCache[cacheKey];
    const themeColors = this.getThemeColors();
    const gradient = this.ctx.createLinearGradient(0, 0, this.width, 0);
    gradient.addColorStop(0, themeColors.gradientColors[0]);
    gradient.addColorStop(0.5, themeColors.gradientColors[1]);
    gradient.addColorStop(1, themeColors.gradientColors[2]);
    this.gradientCache[cacheKey] = gradient;
    return gradient;
  }

  renderFrame(isPlaying, isConnected, hasSignal, currentSpeaker, isPlayingAudio) {
    const now = performance.now();
    const elapsed = now - this.lastFrameTime;
    if (elapsed < this.frameInterval) {
      this.animationFrame = requestAnimationFrame(() =>
        this.renderFrame(isPlaying, isConnected, hasSignal, currentSpeaker, isPlayingAudio)
      );
      return;
    }
    if (elapsed > this.frameInterval * 2) console.warn("Rendering delay detected:", elapsed, "ms");
    this.lastFrameTime = now - (elapsed % this.frameInterval);
    if (!this.ctx) return;
    this.ctx.clearRect(0, 0, this.width, this.height);
    // Transparent canvas -- the panel underneath provides the surface, so
    // the visualizer reads as part of the glass rather than a hard inset.
    if (!hasSignal || !isConnected) {
      this.renderStatic(!hasSignal, !isConnected);
    } else {
      this.renderBars(isPlaying, currentSpeaker, isPlayingAudio);
    }
    this.animationFrame = requestAnimationFrame(() =>
      this.renderFrame(isPlaying, isConnected, hasSignal, currentSpeaker, isPlayingAudio)
    );
  }

  renderStatic(noSignal, noConnection) {
    const skipFactor = 2;
    const mid = this.height / 2;
    for (let i = 0; i < this.width; i += skipFactor) {
      const staticHeight = Math.random() * this.height * 0.08;
      this.ctx.fillStyle = noSignal ? `rgba(150, 150, 150, 0.25)` : `rgba(255, 80, 80, 0.25)`;
      this.ctx.fillRect(i, mid - staticHeight / 2, skipFactor - 0.5, staticHeight);
    }
  }

  // Speaker-tinted accent color, returned as "r,g,b". Used to colorize the
  // mirrored bars when audio is playing.
  getSpeakerAccent(currentSpeaker, isPlayingAudio) {
    if (!isPlayingAudio) return null;
    if (currentSpeaker === "Ada") return "248,113,113";
    if (currentSpeaker === "Ved") return "74,222,128";
    if (currentSpeaker === "system") return "165,180,252";
    return null;
  }

  renderBars(isPlaying, currentSpeaker, isPlayingAudio) {
    const themeColors = this.getThemeColors();
    const speakerAccent = this.getSpeakerAccent(currentSpeaker, isPlayingAudio);

    // Default bar tint when no speaker accent applies.
    const defaultBar = Array.isArray(themeColors.barColorDefault)
      ? themeColors.barColorDefault[0]
      : themeColors.barColorDefault;

    let useReal = false;
    if (this.analyser && this.freqData) {
      this.analyser.getByteFrequencyData(this.freqData);
      useReal = true;
    }

    const timeValue = performance.now() * 0.001;
    const mid = this.height / 2;
    const maxBarHeight = this.height * 0.92; // total peak-to-peak
    const halfMax = maxBarHeight / 2;
    const radius = this.barWidth / 2;

    for (let i = 0; i < this.barPositions.length; i++) {
      const { x, position } = this.barPositions[i];
      let amp; // 0..1

      if (useReal && isPlaying) {
        const usableBins = Math.floor(this.freqData.length * 0.75);
        const binIdx = Math.min(usableBins - 1, Math.floor(i * usableBins / this.barPositions.length));
        const magnitude = this.freqData[binIdx] / 255;
        amp = Math.pow(magnitude, 0.7);
      } else if (isPlaying) {
        amp = 0.1 +
          (Math.sin(position * 10 + timeValue * 3) * 0.2 +
           Math.sin(position * 20 + timeValue * 2) * 0.3 +
           Math.sin(position * 5 + timeValue) * 0.2) * 0.5;
        if (i % 3 === 0) amp += (Math.random() * 0.04) - 0.02;
        if (isPlayingAudio) amp *= 1.3;
        amp = Math.max(0.02, Math.min(1, amp));
      } else {
        // Paused: gentle breathing baseline across the strip.
        amp = 0.03 + Math.sin(position * 4 + timeValue * 0.8) * 0.015 +
              (i % 7 === 0 ? Math.random() * 0.02 : 0);
      }

      const halfHeight = Math.max(radius, amp * halfMax);
      const intensity = Math.min(1, amp * 1.1 + 0.15);

      // Compose color. Speaker accents pop; otherwise fall back to theme.
      let fill;
      if (speakerAccent) {
        fill = `rgba(${speakerAccent}, ${intensity})`;
      } else {
        fill = defaultBar.replace('{opacity}', intensity.toFixed(3));
      }
      this.ctx.fillStyle = fill;
      this.fillRoundedBar(x, mid - halfHeight, this.barWidth, halfHeight * 2, radius);
    }
  }

  // Rounded-cap bar drawn via two semicircles + a rect. Cheaper than
  // ctx.roundRect (unsupported in some older browsers) and visually nicer
  // than square caps.
  fillRoundedBar(x, y, w, h, r) {
    if (h <= 0) return;
    const radius = Math.min(r, w / 2, h / 2);
    this.ctx.beginPath();
    this.ctx.moveTo(x + radius, y);
    this.ctx.lineTo(x + w - radius, y);
    this.ctx.quadraticCurveTo(x + w, y, x + w, y + radius);
    this.ctx.lineTo(x + w, y + h - radius);
    this.ctx.quadraticCurveTo(x + w, y + h, x + w - radius, y + h);
    this.ctx.lineTo(x + radius, y + h);
    this.ctx.quadraticCurveTo(x, y + h, x, y + h - radius);
    this.ctx.lineTo(x, y + radius);
    this.ctx.quadraticCurveTo(x, y, x + radius, y);
    this.ctx.closePath();
    this.ctx.fill();
  }

  start(isPlaying, isConnected, hasSignal, currentSpeaker, isPlayingAudio) {
    this.stop();
    this.renderFrame(isPlaying, isConnected, hasSignal, currentSpeaker, isPlayingAudio);
  }

  stop() {
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }
  }

  updateTheme(colorTheme) {
    this.colorTheme = colorTheme;
  }

  resize(width, height) {
    this.width = width;
    this.height = height;
    this.canvas.width = width;
    this.canvas.height = height;
    this.barCount = Math.floor(this.width / (this.barWidth + this.barGap));
    this.barPositions = new Array(this.barCount).fill(0).map((_, i) => ({
      x: i * (this.barWidth + this.barGap),
      position: i / this.barCount
    }));
    this.gradientCache = {};
  }
}
